// src/services/agencyCustomerService.js
import apiService from '../utils/apiInterceptor';

/**
 * Service for managing agency-customer relationships
 * Designed to work with Laravel backend API
 */
const agencyCustomerService = {
  /**
   * Get customers managed by an agency
   * @param {number} agencyId - Agency user ID
   * @param {Object} params - Optional query parameters
   * @returns {Promise<Object>} Promise resolving to customers data
   */
  getAgencyCustomers: async (agencyId, params = {}) => {
    try {
      return await apiService.get(`/admin/agency/${agencyId}/customers`, { params });
    } catch (error) {
      console.error(`Error fetching customers for agency ${agencyId}:`, error);
      throw error;
    }
  },

  /**
   * Get agencies that manage a customer
   * @param {number} customerId - Customer user ID
   * @returns {Promise<Object>} Promise resolving to agencies data
   */
  getCustomerAgencies: async (customerId) => {
    try {
      return await apiService.get(`/admin/customer/${customerId}/agencies`);
    } catch (error) {
      console.error(`Error fetching agencies for customer ${customerId}:`, error);
      throw error;
    }
  },

  /**
   * Assign customers to an agency
   * @param {number} agencyId - Agency user ID
   * @param {Array} customerIds - Array of customer user IDs
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  assignCustomersToAgency: async (agencyId, customerIds) => {
    try {
      return await apiService.post(`/admin/agency/${agencyId}/customers`, { customer_ids: customerIds });
    } catch (error) {
      console.error(`Error assigning customers to agency ${agencyId}:`, error);
      throw error;
    }
  },

  /**
   * Update agency's customer assignments
   * @param {number} agencyId - Agency user ID
   * @param {Array} customerIds - Array of customer user IDs
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  updateAgencyCustomers: async (agencyId, customerIds) => {
    try {
      return await apiService.post(`/admin/agency/${agencyId}/customers`, { customer_ids: customerIds });
    } catch (error) {
      console.error(`Error updating customers for agency ${agencyId}:`, error);
      throw error;
    }
  },

  /**
   * Remove a customer from an agency
   * @param {number} agencyId - Agency user ID
   * @param {number} customerId - Customer user ID
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  removeCustomerFromAgency: async (agencyId, customerId) => {
    try {
      return await apiService.delete(`/admin/agency/${agencyId}/customer/${customerId}`);
    } catch (error) {
      console.error(`Error removing customer ${customerId} from agency ${agencyId}:`, error);
      throw error;
    }
  },

  /**
   * Check if an agency manages a specific customer
   * @param {number} agencyId - Agency user ID
   * @param {number} customerId - Customer user ID
   * @returns {Promise<boolean>} Promise resolving to whether the agency manages the customer
   */
  doesAgencyManageCustomer: async (agencyId, customerId) => {
    try {
      const response = await apiService.get(`/admin/agency/${agencyId}/customer/${customerId}/check`);
      return response.manages || false;
    } catch (error) {
      console.error(`Error checking if agency ${agencyId} manages customer ${customerId}:`, error);
      return false;
    }
  },

  /**
   * Get all available customers for an agency to manage
   * @param {number} agencyId - Agency user ID
   * @param {Object} params - Search and pagination parameters
   * @returns {Promise<Object>} Promise resolving to available customers data
   */
  getAvailableCustomers: async (agencyId, params = {}) => {
    try {
      return await apiService.get(`/admin/agency/${agencyId}/available-customers`, { params });
    } catch (error) {
      console.error(`Error fetching available customers for agency ${agencyId}:`, error);
      throw error;
    }
  }
};

export default agencyCustomerService;